import { LiveAnnouncer } from '@angular/cdk/a11y';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, NgZone, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';

import { TourPlacement } from 'app/shared/models/tutorial.model';
import { TutorialService } from 'app/shared/services/tutorial.service';

/** Rectangle of the spotlighted target (viewport coordinates), with the padding already applied. */
interface Spotlight {
    top: number;
    left: number;
    width: number;
    height: number;
}

/**
 * The computed position for the step card.
 *
 * `dock` overrides `top`/`left` and lets CSS place the card: `center` for a target-less step on a roomy screen,
 * `top`/`bottom` for a narrow screen, where a floating card beside a control is not an option and the card
 * instead takes the edge of the screen the target is NOT near.
 */
export interface CardPosition {
    top: number;
    left: number;
    dock: 'top' | 'bottom' | 'center' | null;
    /**
     * Which side of the target the card took. Fed back into the next measure of the same step so the choice
     * sticks: the card's rendered height changes as its text lands and as fonts load, and a fresh above/below
     * decision on every pass had the card hopping over the target until it settled.
     */
    side?: 'above' | 'below';
    /**
     * Distance from the viewport bottom, set when the card floats ABOVE its target. The edge that must stay put
     * there is the bottom one — the gap to the target — and a card positioned by `top` slides down as its own
     * height settles. Positioned by `bottom` it grows upward and the gap holds.
     */
    bottom?: number;
}

/** How far the app's fixed bars intrude on the viewport, top and bottom. */
export interface ChromeInsets {
    top: number;
    bottom: number;
}

/** No chrome — what a desktop layout measures to, since HOMER's header scrolls with the page there. */
const NO_INSETS: ChromeInsets = { top: 0, bottom: 0 };

/**
 * HOMER's top bar. It is only `position: fixed` below the `lg` breakpoint; above it, it scrolls with the page
 * content and measures to zero. HOMER has no fixed bottom bar.
 */
const CHROME_TOP_SELECTOR = '#pcgMainNavigation';

/** Gap (px) between the spotlight and the card. */
const CARD_GAP = 12;
/** Approximate card width used for overflow math. Must match $card-width in the scss. */
const CARD_W = 320;
/** Approximate card height used for overflow math before the card has rendered. */
const CARD_H = 180;
/**
 * At or below this viewport width the card docks to the top or bottom edge instead of floating beside its
 * target. A 320px card plus gaps cannot sit beside anything on a phone or a small portrait tablet.
 */
const DOCK_BREAKPOINT = 640;
/**
 * On a narrow screen, a target taller than this share of the band is always docked BELOW: it has no roomier
 * side, and its top edge — a list's heading, a card's title — is what the step is naming.
 */
const TALL_DOCK_RATIO = 0.5;
/**
 * Padding (px) added around the target when cutting the spotlight hole. Has to clear the ring's own 3px border,
 * which `box-sizing: border-box` draws INSIDE this rect.
 */
const SPOTLIGHT_PAD = 10;
/** How far the spotlight is kept inside the viewport, so its outline is never drawn off screen. */
const SPOTLIGHT_EDGE_INSET = 4;
/**
 * How long (ms) a target must hold still before a follow-up measure is allowed to MOVE THE CARD. The ring tracks
 * the target every frame; the card is what the user is reading, and a card that re-places itself on every frame
 * of a drawer's slide-in visibly shakes.
 */
const CARD_SETTLE_MS = 120;
/**
 * How many consecutive unchanged frames a freshly entered step's target must show before the card is placed.
 * One is not enough: the tail of an ease-out moves less than the change threshold per frame.
 */
const ENTRY_STILL_FRAMES = 3;
/**
 * How long (ms) after entering a step a target may still be missing before the card falls back to the centered,
 * target-less position. Until then the card stays where it is, so a target that renders a frame or two late does
 * not cost a jump to the center and back.
 */
const MISSING_TARGET_DEADLINE_MS = 480;
/** A target taller than this share of the band is anchored at its top instead of centered when scrolled to. */
const TALL_TARGET_RATIO = 0.8;
/** Breathing room kept between the target and the app's fixed bars when scrolling it into view. */
const BAND_MARGIN = 24;

/**
 * Measures how far the app's fixed bars intrude on the viewport. Only a FIXED, visible bar counts.
 *
 * `documentElement.clientHeight`, not `window.innerHeight`: `getBoundingClientRect` is in layout-viewport
 * coordinates, and on a mobile browser innerHeight is the visual viewport, which differs by the URL bar.
 */
export function measureChromeInsets(): ChromeInsets {
    if (typeof document === 'undefined') { return NO_INSETS; }

    const viewportHeight = document.documentElement.clientHeight;
    const el = document.querySelector(CHROME_TOP_SELECTOR);
    if (
        !el
        || !isPinned(el)
    ) { return NO_INSETS; }

    const rect = el.getBoundingClientRect();
    if (rect.height <= 0) { return NO_INSETS; }
    return { top: Math.max(0, Math.min(rect.bottom, viewportHeight)), bottom: 0 };
}

/** Whether `el` is held in place by the viewport — itself fixed, or inside something that is. */
function isPinned(el: Element): boolean {
    for (let node: Element | null = el; node; node = node.parentElement) {
        if (getComputedStyle(node).position === 'fixed') { return true; }
    }
    return false;
}

/**
 * Computes where the step card should sit. Everything is positioned inside the SAFE BAND — the viewport minus
 * the app's fixed bars — so a card is never placed under the top bar.
 *
 * - No target: centered in the band.
 * - Narrow screen with a target: docked to whichever edge of the band has more room, so the card never covers
 *   the control it is describing (or to the step's `narrowDock`, when set).
 * - Otherwise: `left`/`right` place the card beside the target (vertically centered) when that side has room —
 *   used to keep it clear of a right-anchored drawer — falling back to vertical placement; `top`/`bottom`/`auto`
 *   prefer below the target, flip above when there is no room, and clamp into the band.
 *
 * Exported as a pure function so the placement rules are unit-testable without a DOM.
 *
 * @param rect The measured target rectangle, or null for a target-less step.
 * @param vw Viewport width in px.
 * @param vh Viewport height in px.
 * @param placement The step's preferred placement.
 * @param cardH The card's real rendered height, when known.
 * @param insets How far the app's fixed bars intrude, from {@link measureChromeInsets}.
 * @param narrowDock The step's chosen dock edge for a narrow screen. Null to choose automatically.
 * @param preferredSide The side already taken on this step, kept while it still fits.
 */
export function computeCardPosition(
    rect: DOMRect | null
    , vw: number
    , vh: number
    , placement: TourPlacement = 'auto'
    , cardH: number = CARD_H
    , insets: ChromeInsets = NO_INSETS
    , narrowDock: 'top' | 'bottom' | null = null
    , preferredSide: 'above' | 'below' | null = null
): CardPosition {
    if (!rect) { return { top: 0, left: 0, dock: 'center' }; }

    const h = cardH || CARD_H;
    // When the band is shorter than the card — a phone in landscape — minTop wins and the card's own max-height
    // keeps it inside; see the scss.
    const bandTop = insets.top;
    const bandBottom = vh - insets.bottom;
    const minTop = bandTop + CARD_GAP;
    const maxTop = Math.max(minTop, bandBottom - h - CARD_GAP);

    if (vw <= DOCK_BREAKPOINT) {
        if (narrowDock) { return { top: 0, left: 0, dock: narrowDock }; }

        if (rect.height > (bandBottom - bandTop) * TALL_DOCK_RATIO) {
            return { top: 0, left: 0, dock: 'bottom', side: 'below' };
        }

        const roomAbove = rect.top - bandTop;
        const roomBelow = bandBottom - rect.bottom;

        // Hysteresis: the target is scrolled to the middle of the band on entry, which puts the two gaps within a
        // few pixels of each other. Re-deciding on every pass flips the card between the top and bottom of the
        // screen on sub-pixel changes, so the side already taken is kept while the card still fits there.
        let dock: 'top' | 'bottom';
        if (
            preferredSide === 'above'
            && roomAbove >= h + CARD_GAP
        ) { dock = 'top'; }
        else if (
            preferredSide === 'below'
            && roomBelow >= h + CARD_GAP
        ) { dock = 'bottom'; }
        else { dock = roomBelow >= roomAbove ? 'bottom' : 'top'; }

        return { top: 0, left: 0, dock, side: dock === 'top' ? 'above' : 'below' };
    }

    // Side placement: beside the target, vertically centered, when the requested side has room.
    if (
        placement === 'left'
        || placement === 'right'
    ) {
        const sideTop = Math.max(minTop, Math.min(rect.top + rect.height / 2 - h / 2, maxTop));
        if (
            placement === 'left'
            && rect.left >= CARD_W + CARD_GAP
        ) { return { top: sideTop, left: rect.left - CARD_W - CARD_GAP, dock: null }; }
        if (
            placement === 'right'
            && vw - rect.right >= CARD_W + CARD_GAP
        ) { return { top: sideTop, left: rect.right + CARD_GAP, dock: null }; }
        // No room on the requested side — fall through to vertical placement.
    }

    // Vertical placement. `top`/`bottom` force that side (honoring the author's intent so a footer step sits fully
    // clear of its button); `auto` prefers below and flips above. Either falls back when there is no room.
    const fitsAbove = rect.top - CARD_GAP - bandTop >= h;
    const fitsBelow = bandBottom - rect.bottom - CARD_GAP >= h;

    let above: boolean;
    if (placement === 'top') { above = fitsAbove || !fitsBelow; }
    else if (
        preferredSide === 'above'
        && fitsAbove
    ) { above = true; }
    else if (
        preferredSide === 'below'
        && fitsBelow
    ) { above = false; }
    else { above = !fitsBelow && fitsAbove; }

    // Clamp into the band rather than trusting the chosen side to have room. When the target is taller than the
    // band neither side fits, and an unclamped `rect.bottom + gap` puts the card below the fold where the user
    // cannot see it at all. Overlapping a very tall target is the lesser evil.
    const top = Math.max(
        minTop
        , Math.min(
            above
                ? rect.top - h - CARD_GAP
                : rect.bottom + CARD_GAP
            , maxTop
        )
    );
    let left = rect.left + rect.width / 2 - CARD_W / 2;
    left = Math.max(CARD_GAP, Math.min(left, vw - CARD_W - CARD_GAP));
    return above
        ? { top, left, dock: null, side: 'above', bottom: vh - (top + h) }
        : { top, left, dock: null, side: 'below' };
}

/**
 * Full-viewport guided-tour overlay. When {@link TutorialService} is active it dims the page, cuts a spotlight
 * hole around the current step's target element, and shows a step card (title, body, progress, Back/Next/Skip).
 *
 * Simulated tours capture all pointer events and keep focus inside the card, so the user drives the tour only
 * through the card and the scripted demo stays in sync. Interactive tours let clicks through the spotlight hole
 * to the real element beneath and leave focus alone, so the user can operate the real page.
 *
 * Mounted once at the app shell, outside every transformed ancestor, so `position: fixed` stays
 * viewport-relative.
 */
@Component({
    selector: 'pcg-tutorial-overlay',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonModule],
    templateUrl: './tutorial-overlay.component.html',
    styleUrl: './tutorial-overlay.component.scss',
    host: {
        // No resize or scroll binding: the tracking loop reads the target every frame and treats a viewport
        // change as a fresh entry to the step, which covers resize, rotation, and any scroll container.
        '(document:keydown.escape)': 'onEsc()',
        '(document:keydown.enter)': 'onNext($event)',
        '(document:keydown.arrowright)': 'onNext($event)',
        '(document:keydown.arrowleft)': 'onBack($event)'
    }
})
export class TutorialOverlayComponent {
    //#region Injections
    protected readonly tutorial = inject(TutorialService);
    private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly announcer = inject(LiveAnnouncer);
    private readonly zone = inject(NgZone);
    //#endregion

    //#region State
    /** The measured spotlight rect (null for a target-less/centered step). */
    readonly spotlight = signal<Spotlight | null>(null);

    /** The computed step-card position. */
    readonly card = signal<CardPosition>({ top: 0, left: 0, dock: 'center' });

    /** Whether the running tour lets the user operate the real page. */
    readonly interactive = computed(() => !!this.tutorial.definition()?.interactive);

    /**
     * Clip-path (applied to the interactive click-blocker) that covers the whole viewport EXCEPT the spotlight
     * rect. Clipped-out regions are not hit-testable, so the dimmed area around the highlighted target becomes
     * unclickable while the target itself passes clicks straight through to the real page. Null with no spotlight
     * (a target-less/centered step has nothing to lock to). A single polygon with a zero-width seam to the hole.
     */
    readonly blockerClip = computed(() => {
        const s = this.spotlight();
        if (!s) { return null; }
        const l = s.left;
        const t = s.top;
        const r = s.left + s.width;
        const b = s.top + s.height;
        return `polygon(0 0, 0 100%, ${l}px 100%, ${l}px ${t}px, ${r}px ${t}px, `
            + `${r}px ${b}px, ${l}px ${b}px, ${l}px 100%, 100% 100%, 100% 0)`;
    });

    /** The 1-based step number for the progress label. */
    readonly stepNumber = computed(() => this.tutorial.stepIndex() + 1);

    /** Whether to show the "N / total" progress label for the current tour. */
    readonly showProgress = computed(() => !this.tutorial.definition()?.hideProgress);

    /**
     * The "N / total" progress label. A multi-page tour built from separate single-step definitions can supply
     * `progressCurrent`/`progressTotal` so it reads its overall position (e.g. "2 / 6") instead of "1 / 1".
     */
    readonly progressLabel = computed(() => {
        const def = this.tutorial.definition();
        if (def?.progressCurrent && def?.progressTotal) { return `${def.progressCurrent} / ${def.progressTotal}`; }
        return `${this.stepNumber()} / ${this.tutorial.stepCount()}`;
    });
    //#endregion

    //#region Tracking
    /**
     * One loop, one frame at a time, is the whole placement engine. Reading the target once per frame covers a
     * scroll in any container, a resize, a drawer sliding in, and a late layout alike — they are all just "the
     * rect is not where it was". Costs one selector query and one rect read per frame while a tour is on screen,
     * and nothing the rest of the time.
     *
     * Per frame the loop: finds the step's target; on the first frame it is laid out after entering the step,
     * scrolls it into the band and draws the ring; places the card once the target has held still; and on any
     * later frame where the target moved, moves the ring immediately and holds the card until it settles.
     */
    private trackFrame = 0;

    /** The target's last drawn union rect, so a frame can tell it moved. */
    private lastTargetRect: { top: number; left: number; right: number; bottom: number } | null = null;

    /** Set on entering a step and on a viewport change: the next frame with the target laid out re-enters it. */
    private entryPending = false;

    /** Set with entryPending; the next frame announces the step (and focuses the card, for simulated tours). */
    private announcePending = false;

    /** When the current step was entered, for the missing-target deadline. */
    private enteredAt = 0;

    /** Whether the missing-target deadline has been handled for this step. */
    private deadlineJudged = false;

    /** A card placement waiting for the target to hold still, and when the rect last changed. */
    private pendingCard: CardPosition | null = null;
    private rectChangedAt = 0;

    /**
     * True from the entry frame until the target first moves or the card is committed: the card may land after
     * ENTRY_STILL_FRAMES unchanged frames rather than waiting the full CARD_SETTLE_MS. Any movement after entry
     * disarms this and the standard settle rule takes over.
     */
    private entryCommitArmed = false;

    /** Consecutive frames on which the target's rect has not changed. */
    private stillFrames = 0;

    /** Viewport size as of the last frame, so a resize or rotation re-enters the step. */
    private lastViewport = { vw: 0, vh: 0 };

    /** The side of the target the card took on this step, kept until the step changes. */
    private cardSide: 'above' | 'below' | null = null;
    //#endregion

    constructor() {
        // Simulated tours: focus cannot leave the card while the tour runs. Capturing, so it sees focus landing
        // anywhere in the page, including inside components that stop propagation. Interactive tours are left
        // alone — their steps have the user typing or scanning into the real page.
        const onFocusIn = (event: FocusEvent) => {
            if (
                !this.tutorial.active()
                || this.interactive()
            ) { return; }
            const target = event.target as Node | null;
            if (
                target
                && this.hostRef.nativeElement.contains(target)
            ) { return; }
            this.focusCard();
        };
        document.addEventListener('focusin', onFocusIn, true);
        this.destroyRef.onDestroy(() => document.removeEventListener('focusin', onFocusIn, true));

        // Nothing may outlive the overlay: a loop still running would keep measuring a detached target.
        this.destroyRef.onDestroy(() => this.stopTracking());

        // The loop runs for exactly as long as a tour is on screen.
        effect(() => {
            if (this.tutorial.active()) { this.startTracking(); }
            else { this.stopTracking(); }
        });

        // Flag the body while an interactive tour runs so global styles can lift real overlays (e.g. the Actions
        // nav popover) above the tour's dim/card. Removed when the tour ends or the overlay is destroyed.
        effect(() => {
            const interactiveTour = this.tutorial.active() && this.interactive();
            document.body.classList.toggle('pcg-tour-interactive', interactiveTour);
        });
        this.destroyRef.onDestroy(() => document.body.classList.remove('pcg-tour-interactive'));

        // Entering a step arms the loop. Nothing is measured here: the step's beforeEnter hook may still be
        // rendering. Also depends on the current step object, not just its index: an event-driven tour can swap
        // one single-step definition for another (both at index 0), which leaves stepIndex unchanged.
        effect(() => {
            this.tutorial.stepIndex();
            this.tutorial.currentStep();
            if (!this.tutorial.active()) {
                this.spotlight.set(null);
                return;
            }
            this.enterStep();
        });
    }

    //#region Measurement
    /**
     * Measures the current target and recomputes the spotlight and card position.
     *
     * @param bringIntoView Whether to also scroll the target into view. Only true on step entry: scrolling on a
     * follow-up measure would fight whatever moved the page — including the user.
     * @param isDeadline True once the target has been missing past its deadline: the card falls back to the
     * centered, target-less position instead of staying where it is.
     * @param deferCard True when the ring must update now but the card should wait until the target holds still.
     */
    measure(
        bringIntoView: boolean = false
        , isDeadline: boolean = false
        , deferCard: boolean = false
    ): void {
        const step = this.tutorial.currentStep();
        if (!step) {
            this.spotlight.set(null);
            return;
        }

        const vw = document.documentElement.clientWidth || window.innerWidth;
        const vh = document.documentElement.clientHeight || window.innerHeight;
        // Re-measured on every pass, not cached: the top bar becomes fixed or static as the viewport crosses lg.
        const insets = measureChromeInsets();
        this.publishInsets(insets, vh);

        const els = step.targetSelector
            ? Array.from(document.querySelectorAll(step.targetSelector))
            : [];

        if (els.length === 0) {
            this.spotlight.set(null);
            // A target that is expected but has not rendered yet is not a target-less step. Centering the card
            // now and placing it again when the target lands a frame later is a jump on every step that swaps
            // the screen underneath it. Leave the card where it is until the deadline.
            if (
                step.targetSelector
                && !isDeadline
            ) { return; }
            this.card.set(computeCardPosition(null, vw, vh, 'auto', undefined, insets));
            return;
        }

        if (bringIntoView) { this.bringIntoBand(els, insets, vh); }

        // Spotlight the union of every element matching the selector, so a step can highlight a group that has no
        // single wrapping element (e.g. a search field plus its two adjacent buttons).
        const rects = els.map(e => e.getBoundingClientRect());
        let top = Math.min(...rects.map(r => r.top));
        let left = Math.min(...rects.map(r => r.left));
        let right = Math.max(...rects.map(r => r.right));
        let bottom = Math.max(...rects.map(r => r.bottom));

        // A target in the DOM but not laid out yet reports 0x0 at 0,0 for a frame whenever a step swaps the
        // screen underneath. Placing from that rect throws the card to the far left and back.
        if (
            right - left <= 0
            || bottom - top <= 0
        ) { return; }

        this.lastTargetRect = { top, left, right, bottom };

        // Clamp to the scroll container that clips the target: a long list of rows scrolled out of view still
        // reports its full laid-out rect, which would otherwise spotlight empty space over whatever sits below it.
        const clip = this.clipRectFor(els[0]);
        if (clip) {
            top = Math.max(top, clip.top);
            left = Math.max(left, clip.left);
            right = Math.min(right, clip.right);
            bottom = Math.min(bottom, clip.bottom);
        }

        // The card is placed against the RING, not the bare target, so CARD_GAP is real daylight between them.
        const union = {
            top: top - SPOTLIGHT_PAD
            , left: left - SPOTLIGHT_PAD
            , right: right + SPOTLIGHT_PAD
            , bottom: bottom + SPOTLIGHT_PAD
            , width: Math.max(0, right - left) + SPOTLIGHT_PAD * 2
            , height: Math.max(0, bottom - top) + SPOTLIGHT_PAD * 2
        } as DOMRect;

        // Clamp the padded ring into the VIEWPORT so its outline is always on screen: a full-bleed target would
        // otherwise put its sides at -10px and vw+10px. The viewport, not the band — the overlay paints above the
        // top bar, so a ring drawn over it is fully visible, and clamping to the band would push the ring below a
        // target that sits at the top of its screen.
        const ringTop = Math.max(SPOTLIGHT_EDGE_INSET, top - SPOTLIGHT_PAD);
        const ringLeft = Math.max(SPOTLIGHT_EDGE_INSET, left - SPOTLIGHT_PAD);
        const ringBottom = Math.min(vh - SPOTLIGHT_EDGE_INSET, bottom + SPOTLIGHT_PAD);
        const ringRight = Math.min(vw - SPOTLIGHT_EDGE_INSET, right + SPOTLIGHT_PAD);

        this.spotlight.set({
            top: ringTop
            , left: ringLeft
            , width: Math.max(0, ringRight - ringLeft)
            , height: Math.max(0, ringBottom - ringTop)
        });

        // Use the card's real rendered height so an above/beside placement clears the target (a long body can
        // exceed the static estimate and droop back over it).
        const cardH = (this.hostRef.nativeElement.querySelector('.tut__card') as HTMLElement | null)?.offsetHeight
            || undefined;
        const position = computeCardPosition(
            union
            , vw
            , vh
            , step.placement
            , cardH
            , insets
            , step.narrowDock ?? null
            , this.cardSide
        );
        this.cardSide = position.side ?? this.cardSide;

        if (deferCard) {
            this.pendingCard = position;
        } else {
            this.pendingCard = null;
            this.card.set(position);
        }
    }

    /**
     * Publishes the measured band to CSS, for the placements the stylesheet owns: a docked card's distance from
     * its edge, and the height cap that keeps a long step readable on a short landscape screen.
     */
    private publishInsets(
        insets: ChromeInsets
        , vh: number
    ): void {
        const host = this.hostRef.nativeElement.style;
        host.setProperty('--tut-inset-top', `${Math.round(insets.top)}px`);
        host.setProperty('--tut-inset-bottom', `${Math.round(insets.bottom)}px`);
        host.setProperty('--tut-band-height', `${Math.round(Math.max(0, vh - insets.top - insets.bottom))}px`);
    }

    /** Arms the loop for a freshly entered step. */
    private enterStep(): void {
        this.entryPending = true;
        this.announcePending = true;
        this.enteredAt = Date.now();
        this.deadlineJudged = false;
        // A new step decides its own side; a placement still pending from the last step must not land here.
        this.cardSide = null;
        this.pendingCard = null;
        this.entryCommitArmed = false;
        this.stillFrames = 0;
        this.lastTargetRect = null;
    }

    /**
     * Starts the per-frame loop OUTSIDE the Angular zone. HOMER runs zone.js change detection, which patches
     * `requestAnimationFrame`: inside the zone every frame would run change detection across the whole app — 60
     * times a second for as long as a tour is open, on screens with large eagerly-checked tables. Outside it, an
     * idle frame costs one rect read; when a frame does move the ring or card, the signal write schedules change
     * detection on its own.
     */
    private startTracking(): void {
        if (
            this.trackFrame !== 0
            || typeof requestAnimationFrame === 'undefined'
        ) { return; }
        this.zone.runOutsideAngular(() => {
            const frame = () => {
                this.trackFrame = requestAnimationFrame(frame);
                this.tick();
            };
            this.trackFrame = requestAnimationFrame(frame);
        });
    }

    private stopTracking(): void {
        if (
            this.trackFrame !== 0
            && typeof cancelAnimationFrame !== 'undefined'
        ) { cancelAnimationFrame(this.trackFrame); }
        this.trackFrame = 0;
        this.lastTargetRect = null;
        this.pendingCard = null;
        this.entryPending = false;
        this.announcePending = false;
        this.lastViewport = { vw: 0, vh: 0 };
    }

    /** One frame of the tracking loop. See the field docs under "Tracking" for the whole picture. */
    private tick(): void {
        const step = this.tutorial.currentStep();
        if (!step) { return; }

        if (this.announcePending) {
            // Deferred to a frame so the card for this step has rendered before focus lands on it.
            this.announcePending = false;
            this.announceStep();
        }

        // A resize or rotation is a fresh entry: the target may need scrolling again and the card re-placing.
        const vw = document.documentElement.clientWidth || window.innerWidth;
        const vh = document.documentElement.clientHeight || window.innerHeight;
        if (
            vw !== this.lastViewport.vw
            || vh !== this.lastViewport.vh
        ) {
            const first = this.lastViewport.vw === 0;
            this.lastViewport = { vw, vh };
            if (!first) { this.entryPending = true; }
        }

        const els = step.targetSelector
            ? Array.from(document.querySelectorAll(step.targetSelector))
            : [];
        const rects = els.map(e => e.getBoundingClientRect());
        const union = rects.length === 0
            ? null
            : {
                top: Math.min(...rects.map(r => r.top))
                , left: Math.min(...rects.map(r => r.left))
                , right: Math.max(...rects.map(r => r.right))
                , bottom: Math.max(...rects.map(r => r.bottom))
            };

        if (
            !union
            || union.right - union.left <= 0
            || union.bottom - union.top <= 0
        ) {
            if (!step.targetSelector) {
                // A deliberately target-less step: placed once, on entry.
                if (this.entryPending) {
                    this.entryPending = false;
                    this.measure();
                }
                return;
            }

            // Expected but not there. Hide a ring that would otherwise frame the previous step's target, then wait;
            // past the deadline, fall back to a centered card once.
            if (this.spotlight()) { this.spotlight.set(null); }
            if (
                !this.deadlineJudged
                && Date.now() - this.enteredAt >= MISSING_TARGET_DEADLINE_MS
            ) {
                this.deadlineJudged = true;
                this.measure(false, true);
            }
            return;
        }

        const now = Date.now();

        if (this.entryPending) {
            // First frame with the target laid out since entering: scroll it into the band and draw the ring now.
            // The card is held until the target has stopped moving — see entryCommitArmed.
            this.entryPending = false;
            this.measure(true, false, true);
            this.rectChangedAt = now;
            this.entryCommitArmed = true;
            this.stillFrames = 0;
            return;
        }

        const last = this.lastTargetRect;
        const moved = !last
            || Math.abs(union.top - last.top) > 0.5
            || Math.abs(union.left - last.left) > 0.5
            || Math.abs(union.right - last.right) > 0.5
            || Math.abs(union.bottom - last.bottom) > 0.5;

        if (moved) {
            // The ring follows now; the card is held. Movement after entry means the target is animating, so the
            // quick entry commit no longer applies.
            this.measure(false, false, true);
            this.rectChangedAt = now;
            this.entryCommitArmed = false;
            this.stillFrames = 0;
            return;
        }

        this.stillFrames++;
        const settled = (this.entryCommitArmed && this.stillFrames >= ENTRY_STILL_FRAMES)
            || now - this.rectChangedAt >= CARD_SETTLE_MS;
        if (
            this.pendingCard
            && settled
        ) {
            this.card.set(this.pendingCard);
            this.pendingCard = null;
            this.entryCommitArmed = false;
        }
    }

    /**
     * Scrolls the step's target to roughly the middle of the safe band.
     *
     * `block: 'nearest'` is the minimum scroll that makes an element visible, and "visible" to the browser
     * includes the strip under the fixed top bar, which paints over the page rather than reserving space in it.
     * Two passes: `scrollIntoView` handles the whole chain of scroll containers, then a correction moves the
     * target out from under the bar, which `scrollIntoView` knows nothing about.
     */
    private bringIntoBand(
        els: Element[]
        , insets: ChromeInsets
        , vh: number
    ): void {
        const el = els[0];
        // Judged on the union of every match — the same rect the ring is drawn from. A step that frames two
        // controls (a toggle on each of two rows) judged on the first alone left the second under a footer.
        const unionOf = () => {
            const rects = els.map(e => e.getBoundingClientRect());
            const top = Math.min(...rects.map(r => r.top));
            const bottom = Math.max(...rects.map(r => r.bottom));
            return { top, bottom, height: bottom - top };
        };

        // Where the target can actually be seen: the band between the fixed bars, narrowed to its scroll
        // container's box minus any sticky table header/footer. Judging against the window alone counted a row
        // hidden behind a scrollable table's sticky footer as visible, so the table was never scrolled.
        const visible = this.visibleBoxFor(el, insets, vh);
        const band = Math.max(0, visible.bottom - visible.top);
        const margin = Math.min(BAND_MARGIN, band / 4);
        const current = unionOf();
        const tall = current.height > band * TALL_TARGET_RATIO;

        // Already where it needs to be: leave the page alone. A target that fits is left once its top and bottom
        // clear the margins; one taller than the band is left once its top does, since its bottom never can.
        const minTop = visible.top + margin;
        const maxBottom = visible.bottom - margin;
        const topClear = current.top >= minTop && current.top <= maxBottom;
        const bottomClear = current.bottom <= maxBottom;
        if (
            topClear
            && (
                bottomClear
                || tall
            )
        ) { return; }

        // 'instant', never 'auto': 'auto' defers to the element's CSS scroll-behavior, and an animated scroll
        // leaves the rect measured next reading where the target USED to be.
        el.scrollIntoView({ block: tall ? 'start' : 'center', behavior: 'instant' });

        const rect = unionOf();

        // Negative scrolls up, which moves the target down the screen and out from under the top bar.
        if (rect.top < minTop) {
            this.scrollAncestorsBy(el, rect.top - minTop);
            return;
        }

        // Only pull a target up off the bottom edge when it fits: doing it to something taller than the band
        // trades a hidden bottom edge for a hidden top one.
        if (
            rect.bottom > maxBottom
            && rect.height <= band - margin * 2
        ) { this.scrollAncestorsBy(el, rect.bottom - maxBottom); }
    }

    /**
     * The vertical range in which `el` can actually be seen: the band between the app's fixed bars, narrowed by
     * EVERY ancestor that clips vertical overflow, and by the sticky header and footer of every table it sits
     * in. A PrimeNG scrollable table pins both inside its own scroll box, painting over the first and last rows
     * rather than reserving space for them.
     *
     * Every ancestor, not just the nearest: a row in an expanded sub-table is clipped first by the sub-table's
     * own container, which knows nothing about the outer table's sticky footer sitting over it.
     */
    private visibleBoxFor(
        el: Element
        , insets: ChromeInsets
        , vh: number
    ): { top: number; bottom: number } {
        let top = insets.top;
        let bottom = vh - insets.bottom;

        for (let node = el.parentElement; node; node = node.parentElement) {
            // Only a box whose content overflows it can hide anything. A sub-table's own container is set to clip
            // but fits its rows exactly, and intersecting with it — while it sits half under the outer table's
            // footer — left an empty range, which read as "nothing to judge against" and skipped the scroll.
            if (
                node.scrollHeight > node.clientHeight + 1
                && /(auto|scroll|hidden)/.test(getComputedStyle(node).overflowY)
            ) {
                const box = node.getBoundingClientRect();
                top = Math.max(top, box.top);
                bottom = Math.min(bottom, box.bottom);
            }

            if (node instanceof HTMLTableElement) {
                // Sticky sections pin to the edges of the box the table scrolls in — only when that box actually
                // scrolls. PrimeNG marks every table header sticky, a sub-table's included; against a box that
                // doesn't scroll it pins over nothing, and counting it pushed the range's top past its bottom.
                const scroller = this.clipNodeFor(node);
                if (
                    !scroller
                    || scroller.scrollHeight <= scroller.clientHeight + 1
                ) { continue; }
                const scrollBox = scroller.getBoundingClientRect();
                const thead = node.tHead;
                const tfoot = node.tFoot;
                if (
                    thead
                    && getComputedStyle(thead).position === 'sticky'
                ) { top = Math.max(top, scrollBox.top + thead.offsetHeight); }
                if (
                    tfoot
                    && getComputedStyle(tfoot).position === 'sticky'
                ) { bottom = Math.min(bottom, scrollBox.bottom - tfoot.offsetHeight); }
            }
        }

        return bottom > top ? { top, bottom } : { top: insets.top, bottom: vh - insets.bottom };
    }

    /**
     * Scrolls `el` by `dy` using whatever actually scrolls around it. NOT the window: HOMER's page content scrolls
     * inside `.mat-sidenav-content`, so `window.scrollBy` is a no-op on most screens. Walks outwards with whatever
     * is left over, because the nearest scroller is often already at one end. Every move is instant, for the same
     * reason as in {@link bringIntoBand}.
     */
    private scrollAncestorsBy(
        el: Element
        , dy: number
    ): void {
        let remaining = dy;

        for (let node = el.parentElement; node && Math.abs(remaining) > 1; node = node.parentElement) {
            const style = getComputedStyle(node);
            const scrolls = /(auto|scroll)/.test(style.overflowY) || /(auto|scroll)/.test(style.overflowX);
            if (
                !scrolls
                || node.scrollHeight <= node.clientHeight
            ) { continue; }

            const before = node.scrollTop;
            node.scrollTo({ top: before + remaining, behavior: 'instant' });
            remaining -= node.scrollTop - before;
        }

        if (Math.abs(remaining) > 1) { window.scrollBy({ top: remaining, behavior: 'instant' }); }
    }

    /**
     * The viewport rect of the nearest ancestor that clips overflow (overflow auto/scroll/hidden) — the visible
     * box the target lives in. Null when nothing up the tree clips (the target is bounded only by the viewport).
     */
    private clipRectFor(el: Element): DOMRect | null {
        return this.clipNodeFor(el)?.getBoundingClientRect() ?? null;
    }

    /** The nearest ancestor that clips overflow (overflow auto/scroll/hidden), or null when nothing does. */
    private clipNodeFor(el: Element): HTMLElement | null {
        for (let node = el.parentElement; node; node = node.parentElement) {
            const style = getComputedStyle(node);
            if (
                /(auto|scroll|hidden)/.test(style.overflowY)
                || /(auto|scroll|hidden)/.test(style.overflowX)
            ) { return node; }
        }
        return null;
    }
    //#endregion

    //#region Accessibility
    /**
     * Announces the step and, for simulated tours, moves focus into the card.
     *
     * The announcement carries the position, title and body because focus lands on the same element every step,
     * and screen readers do not reliably re-announce a dialog when only its content changed. Interactive tours
     * keep focus where it is: their steps have the user typing or scanning into the real page.
     */
    private announceStep(): void {
        const step = this.tutorial.currentStep();
        if (!step) { return; }
        if (!this.interactive()) { this.focusCard(); }
        const progress = this.showProgress() ? `Step ${this.progressLabel().replace(' / ', ' of ')}. ` : '';
        this.announcer.announce(`${progress}${step.title}. ${step.body}`, 'polite');
    }

    /** Focuses the step card itself, which is the tour's focus anchor. */
    private focusCard(): void {
        const card = this.hostRef.nativeElement.querySelector('.tut__card') as HTMLElement | null;
        card?.focus({ preventScroll: true });
    }
    //#endregion

    /**
     * Swallow pointer/mouse events on the dim blocker: they must neither reach the page (already handled by the
     * clip-path hole) nor bubble to document-level dismiss listeners. Without this, clicking the dimmed area
     * closes an open dropdown the current step still needs (e.g. the Actions menu on the Orders/Receiving steps),
     * because PrimeNG's overlay treats the bubbled event as an outside click.
     */
    protected absorbBlockerEvent(event: Event): void {
        event.stopPropagation();
    }

    //#region Keyboard
    /**
     * Esc exits simulated tours. Interactive (real-action) tours run alongside real widgets — dropdowns,
     * dialogs, popovers — that use Escape to close, so hijacking Esc to permanently end the tour would kill it
     * every time the user closes one of those. For interactive tours Skip Tutorial is the only explicit exit.
     */
    onEsc(): void {
        if (!this.tutorial.active()) { return; }
        if (this.interactive()) { return; }
        this.tutorial.exit();
    }

    /**
     * Enter or Right Arrow advances to the next step (finishing on the last), so the user can walk the whole
     * tour from the keyboard.
     *
     * `preventDefault` stops the key's default action — Enter activating a focused Next button or submitting a
     * background form, Right Arrow scrolling the page or moving the caret in a focused field — which would
     * otherwise advance twice or disturb the page underneath.
     */
    onNext(event: Event): void {
        if (!this.tutorial.active()) { return; }
        // Steps that advance on a real action hide Next; keyboard advance must mirror that (else Enter/→ would
        // finish/skip an event-driven step).
        if (this.tutorial.currentStep()?.hideNext) { return; }
        event.preventDefault();
        this.advance();
    }

    /**
     * Advances the current step. A step's `onNext` hook (if present) takes over — used by multi-page tours to
     * hand a manual advance to their orchestrator instead of ending this single-step definition. Otherwise the
     * engine advances/finishes normally.
     */
    advance(): void {
        const onNext = this.tutorial.currentStep()?.onNext;
        if (onNext) { onNext(); return; }
        this.tutorial.next();
    }

    /** Left Arrow returns to the previous step. A no-op on the first step. */
    onBack(event: Event): void {
        if (!this.tutorial.active()) { return; }
        event.preventDefault();
        this.tutorial.back();
    }
    //#endregion
}
